/**
 * spaceSheet - the document model of a space's spreadsheets, shared by the
 * browser grid and the server (which imports and exports .xlsx and .csv).
 *
 * A sheet document is one Yjs document:
 *
 * - `sheets` (Y.Map): sheet id → the sheet (Y.Map) with
 *   - `name` (string), `rowCount` / `colCount` (numbers, the extent the
 *     file declared; the grid also grows to every filled cell),
 *   - `cells` (Y.Map): `"<row>:<col>"` (0-based) → {@link SheetCell},
 *   - `colWidths` (Y.Map): `"<col>"` → width in Excel character units.
 * - `sheetOrder` (Y.Array): sheet ids, in tab order.
 *
 * A cell is a plain value replaced whole, so two people typing into the same
 * cell end with one of the two values (last writer wins), never a mix, and
 * people typing into different cells never conflict.
 *
 * Values are text and numbers. A formula keeps its text (`f`, without the
 * "=") and the value the file last computed (`v`); nothing recalculates here.
 *
 * @module spaceSheet
 */
import * as Y from "yjs";

import { parseDelimited } from "./delimitedPreview.ts";

export const SHEET_SHEETS = "sheets";
export const SHEET_ORDER = "sheetOrder";

/** Bounds on what one sheet document holds; Excel's own where it has one. */
export const SHEET_LIMITS = {
  sheets: 50,
  /** Filled cells across the whole workbook. */
  cells: 100_000,
  rows: 1_048_576,
  columns: 16_384,
  /** Characters in one cell. */
  cellText: 32_767,
  sheetName: 31,
} as const;

/** One cell. */
export interface SheetCell {
  /** The value; for a formula, the value the file last computed (null when none). */
  readonly v: string | number | null;
  /** A formula's text, without the leading "=". */
  readonly f?: string;
  /** The file's number format ("#,##0.00", "0%"), kept for the round trip. */
  readonly z?: string;
}

export interface SheetData {
  readonly id: string;
  readonly name: string;
  readonly rowCount: number;
  readonly colCount: number;
  /** Column index → width in Excel character units. */
  readonly colWidths: Readonly<Record<number, number>>;
  /** `"<row>:<col>"` → cell. */
  readonly cells: Readonly<Record<string, SheetCell>>;
}

export interface SheetWorkbook {
  readonly sheets: ReadonlyArray<SheetData>;
}

export class SheetImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SheetImportError";
  }
}

/* ------------------------------------------------------------------ */
/* Addressing                                                          */
/* ------------------------------------------------------------------ */

export const cellKey = (row: number, col: number): string => `${row}:${col}`;

export function parseCellKey(key: string): { readonly row: number; readonly col: number } | null {
  const match = /^(\d+):(\d+)$/.exec(key);
  if (match === null) return null;
  const row = Number(match[1]);
  const col = Number(match[2]);
  if (row >= SHEET_LIMITS.rows || col >= SHEET_LIMITS.columns) return null;
  return { row, col };
}

/** 0 → "A", 25 → "Z", 26 → "AA". */
export function columnLabel(col: number): string {
  let label = "";
  let rest = Math.trunc(col) + 1;
  while (rest > 0) {
    const digit = (rest - 1) % 26;
    label = String.fromCharCode(65 + digit) + label;
    rest = Math.floor((rest - 1) / 26);
  }
  return label;
}

/** "A" → 0, "AA" → 26; null when it is not a column. */
export function columnIndex(label: string): number | null {
  if (!/^[A-Za-z]{1,3}$/.test(label)) return null;
  let index = 0;
  for (const char of label.toUpperCase()) index = index * 26 + (char.charCodeAt(0) - 64);
  return index - 1 < SHEET_LIMITS.columns ? index - 1 : null;
}

/** (2, 1) → "B3". */
export const cellAddress = (row: number, col: number): string => `${columnLabel(col)}${row + 1}`;

/** "B3" (or "$B$3") → { row: 2, col: 1 }; null when it is not one cell. */
export function parseCellAddress(
  address: string,
): { readonly row: number; readonly col: number } | null {
  const match = /^\$?([A-Za-z]{1,3})\$?(\d{1,7})$/.exec(address.trim());
  if (match === null) return null;
  const col = columnIndex(match[1]!);
  const row = Number(match[2]) - 1;
  if (col === null || row < 0 || row >= SHEET_LIMITS.rows) return null;
  return { row, col };
}

/* ------------------------------------------------------------------ */
/* Typing into a cell, and showing it                                  */
/* ------------------------------------------------------------------ */

const NUMBER = /^[-+]?(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?$/;

/**
 * What typing `text` into a cell stores: nothing for empty text, a formula
 * for "=…" (no cached value: it is not computed here), a number for a
 * number, else the text. A number keeps the format the cell had.
 */
export function cellFromInput(text: string, previous?: SheetCell | null): SheetCell | null {
  if (text.length === 0) return null;
  const clipped = text.slice(0, SHEET_LIMITS.cellText);
  if (clipped.startsWith("=") && clipped.length > 1) return { v: null, f: clipped.slice(1) };
  const trimmed = clipped.trim();
  if (NUMBER.test(trimmed)) {
    const value = Number(trimmed);
    if (Number.isFinite(value)) {
      return previous?.z !== undefined && previous.f === undefined
        ? { v: value, z: previous.z }
        : { v: value };
    }
  }
  return { v: clipped };
}

/** The text an editor starts from: the formula with its "=", else the value. */
export function cellInputText(cell: SheetCell | null | undefined): string {
  if (!cell) return "";
  if (cell.f !== undefined) return `=${cell.f}`;
  return cell.v === null ? "" : String(cell.v);
}

const CURRENCY = /[£$€¥]/;

/** A number as the cell's format asks: decimals, grouping, percent, a currency sign. */
export function formatSheetNumber(value: number, format?: string): string {
  if (format === undefined || format === "" || format.toLowerCase() === "general") {
    return Number.isInteger(value) ? String(value) : String(Number(value.toPrecision(12)));
  }
  const section = format.split(";")[0] ?? format;
  const percent = section.includes("%");
  // "0" places always show; "#" places show only when the value needs them.
  const places = /\.([0#]+)/.exec(section)?.[1] ?? "";
  const minimumDecimals = [...places].filter((place) => place === "0").length;
  const grouping = /#,##|0,0/.test(section);
  const currency = CURRENCY.exec(section)?.[0] ?? "";
  const shown = percent ? value * 100 : value;
  const text = Math.abs(shown).toLocaleString("en-US", {
    minimumFractionDigits: minimumDecimals,
    maximumFractionDigits: places.length,
    useGrouping: grouping,
  });
  return `${shown < 0 ? "-" : ""}${currency}${text}${percent ? "%" : ""}`;
}

/** What the grid shows in a cell. A formula without a saved value shows its text. */
export function cellDisplayText(cell: SheetCell | null | undefined): string {
  if (!cell) return "";
  if (cell.v === null) return cell.f !== undefined ? `=${cell.f}` : "";
  return typeof cell.v === "number" ? formatSheetNumber(cell.v, cell.z) : cell.v;
}

/* ------------------------------------------------------------------ */
/* Clipboard                                                           */
/* ------------------------------------------------------------------ */

/**
 * A pasted block as rows of cells. Spreadsheets copy TSV with one trailing
 * newline and quote cells that hold tabs, quotes or newlines; text without a
 * tab or a newline is one cell, taken as it is. Past that one terminator, a
 * body that still ends in a newline ends in an empty row (one empty cell),
 * which the TSV reader would otherwise drop.
 */
export function parseClipboardTable(text: string): string[][] {
  const body = text.replace(/\r?\n$/, "");
  if (!body.includes("\t") && !body.includes("\n") && !body.includes("\r")) return [[body]];
  const { rows } = parseDelimited(body, "\t");
  if (body.endsWith("\n") || body.endsWith("\r")) rows.push([""]);
  return rows;
}

const quoteTsv = (cell: string) =>
  /[\t\n\r"]/.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell;

/**
 * A block of cells as TSV, as spreadsheets paste it: each row ends in a
 * newline, so a last row that is one empty cell survives the paste. A
 * single cell with no tab or newline is written as it is, since
 * {@link parseClipboardTable} takes such text literally.
 */
export function toClipboardTable(rows: ReadonlyArray<ReadonlyArray<string>>): string {
  const only = rows.length === 1 && rows[0]!.length === 1 ? rows[0]![0]! : null;
  if (only !== null && !/[\t\n\r]/.test(only)) return `${only}\n`;
  return rows.map((row) => `${row.map(quoteTsv).join("\t")}\n`).join("");
}

/* ------------------------------------------------------------------ */
/* CSV                                                                 */
/* ------------------------------------------------------------------ */

const cellCount = (workbook: SheetWorkbook) =>
  workbook.sheets.reduce((total, sheet) => total + Object.keys(sheet.cells).length, 0);

/** Refuses a workbook over the limits, in words a person can act on. */
export function assertSheetLimits(workbook: SheetWorkbook): void {
  if (workbook.sheets.length === 0) throw new SheetImportError("The file has no sheets.");
  if (workbook.sheets.length > SHEET_LIMITS.sheets) {
    throw new SheetImportError(
      `The file has ${workbook.sheets.length} sheets; the limit is ${SHEET_LIMITS.sheets}.`,
    );
  }
  const cells = cellCount(workbook);
  if (cells > SHEET_LIMITS.cells) {
    throw new SheetImportError(
      `The file has ${cells.toLocaleString("en-US")} filled cells; the limit is ${SHEET_LIMITS.cells.toLocaleString("en-US")}.`,
    );
  }
}

/** A CSV (or TSV) file as a one-sheet workbook. Numbers become numbers. */
export function workbookFromDelimited(
  text: string,
  delimiter: "," | "\t",
  name = "Sheet1",
): SheetWorkbook {
  const { rows } = parseDelimited(text, delimiter);
  const cells: Record<string, SheetCell> = {};
  let colCount = 0;
  let filled = 0;
  rows.forEach((row, rowIndex) => {
    if (rowIndex >= SHEET_LIMITS.rows) {
      // Trailing empty lines are harmless; a value past the last row would be lost.
      if (row.some((text) => text.length > 0)) {
        throw new SheetImportError(
          `The file has values past row ${SHEET_LIMITS.rows.toLocaleString("en-US")}, the last a sheet holds.`,
        );
      }
      return;
    }
    colCount = Math.max(colCount, Math.min(row.length, SHEET_LIMITS.columns));
    row.forEach((text, colIndex) => {
      const cell = cellFromInput(text);
      if (cell === null) return;
      if (colIndex >= SHEET_LIMITS.columns) {
        throw new SheetImportError(
          `The file has values past column ${SHEET_LIMITS.columns.toLocaleString("en-US")}, the last a sheet holds.`,
        );
      }
      filled += 1;
      if (filled > SHEET_LIMITS.cells) {
        throw new SheetImportError(
          `The file has more than ${SHEET_LIMITS.cells.toLocaleString("en-US")} filled cells.`,
        );
      }
      // A CSV formula is text someone typed, not a formula Excel computed.
      cells[cellKey(rowIndex, colIndex)] = cell.f !== undefined ? { v: text } : cell;
    });
  });
  return {
    sheets: [
      {
        id: "s1",
        name: sheetNameFrom(name, []),
        rowCount: Math.min(rows.length, SHEET_LIMITS.rows),
        colCount,
        colWidths: {},
        cells,
      },
    ],
  };
}

/** The workbook's first sheet as CSV. */
export function workbookToDelimited(workbook: SheetWorkbook, delimiter: "," | "\t" = ","): string {
  const sheet = workbook.sheets[0];
  if (!sheet) return "";
  const { rows, cols } = sheetExtent(sheet);
  const quote = (cell: string) =>
    cell.includes(delimiter) || /[\n\r"]/.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell;
  const lines: string[] = [];
  for (let row = 0; row < rows; row += 1) {
    const line: string[] = [];
    for (let col = 0; col < cols; col += 1)
      line.push(quote(cellDisplayText(sheet.cells[cellKey(row, col)])));
    lines.push(line.join(delimiter));
  }
  return lines.join("\n");
}

/** Rows and columns that hold a value (the last filled one + 1). */
export function sheetExtent(sheet: Pick<SheetData, "cells">): {
  readonly rows: number;
  readonly cols: number;
} {
  let rows = 0;
  let cols = 0;
  for (const key of Object.keys(sheet.cells)) {
    const at = parseCellKey(key);
    if (at === null) continue;
    rows = Math.max(rows, at.row + 1);
    cols = Math.max(cols, at.col + 1);
  }
  return { rows, cols };
}

/**
 * Strips apostrophes at either end, which Excel refuses in a sheet name. An
 * index scan, not `/^'+|'+$/`: that pattern backtracks quadratically on a long
 * run of apostrophes that does not reach the end of the string.
 */
function trimApostrophes(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === "'") start += 1;
  while (end > start && value[end - 1] === "'") end -= 1;
  return value.slice(start, end);
}

/** Characters Excel refuses in a sheet name, and control characters. */
// oxlint-disable-next-line no-control-regex -- control characters are refused on purpose
const SHEET_NAME_REFUSED = /[\\/?*[\]:\u0000-\u001f\u007f]/;

/** Already a name Excel accepts: kept exactly, so formulas that name the sheet still find it. */
const isExcelSheetName = (name: string): boolean =>
  name.length > 0 &&
  name.length <= SHEET_LIMITS.sheetName &&
  name.trim().length > 0 &&
  !SHEET_NAME_REFUSED.test(name) &&
  !name.startsWith("'") &&
  !name.endsWith("'");

/**
 * A sheet name Excel accepts, unique among `taken` (case-insensitive). A name
 * Excel already accepts is kept as it is (spaces included: a formula such as
 * `'Q3  cash'!A1` must keep finding its sheet); any other is cleaned.
 */
export function sheetNameFrom(raw: string, taken: ReadonlyArray<string>): string {
  const base = isExcelSheetName(raw) ? raw : cleanSheetName(raw);
  const used = new Set(taken.map((name) => name.toLowerCase()));
  if (!used.has(base.toLowerCase())) return base;
  for (let suffix = 2; ; suffix += 1) {
    const tail = ` (${suffix})`;
    const name = `${base.slice(0, SHEET_LIMITS.sheetName - tail.length)}${tail}`;
    if (!used.has(name.toLowerCase())) return name;
  }
}

/** A name Excel refuses, made one it accepts: refused characters become spaces, runs of space fold. */
function cleanSheetName(raw: string): string {
  const cleaned = trimApostrophes(
    raw
      .replace(/[\\/?*[\]:]/g, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );
  // Cutting to the length limit can leave an apostrophe (or a space) at the new end.
  return trimApostrophes(cleaned.slice(0, SHEET_LIMITS.sheetName).trimEnd()).trimEnd() || "Sheet";
}

/* ------------------------------------------------------------------ */
/* Yjs                                                                 */
/* ------------------------------------------------------------------ */

type YSheet = Y.Map<unknown>;

const sheetsMap = (doc: Y.Doc) => doc.getMap<YSheet>(SHEET_SHEETS);
const orderArray = (doc: Y.Doc) => doc.getArray<string>(SHEET_ORDER);

const isSheetCell = (value: unknown): value is SheetCell => {
  if (value === null || typeof value !== "object") return false;
  const cell = value as Record<string, unknown>;
  return (
    (cell.v === null || typeof cell.v === "string" || typeof cell.v === "number") &&
    (cell.f === undefined || typeof cell.f === "string") &&
    (cell.z === undefined || typeof cell.z === "string")
  );
};

const toCell = (cell: SheetCell): SheetCell => ({
  v: cell.v,
  ...(cell.f !== undefined ? { f: cell.f } : {}),
  ...(cell.z !== undefined ? { z: cell.z } : {}),
});

function writeSheet(sheet: SheetData): YSheet {
  const map = new Y.Map<unknown>();
  map.set("name", sheet.name);
  map.set("rowCount", sheet.rowCount);
  map.set("colCount", sheet.colCount);
  const cells = new Y.Map<SheetCell>();
  for (const [key, cell] of Object.entries(sheet.cells)) {
    if (parseCellKey(key) !== null) cells.set(key, toCell(cell));
  }
  map.set("cells", cells);
  const widths = new Y.Map<number>();
  for (const [col, width] of Object.entries(sheet.colWidths)) {
    if (Number.isFinite(width) && width > 0) widths.set(col, width);
  }
  map.set("colWidths", widths);
  return map;
}

/** Fill an empty document with a workbook, in one transaction. */
export function writeWorkbook(doc: Y.Doc, workbook: SheetWorkbook, origin: unknown = null): void {
  doc.transact(() => {
    const sheets = sheetsMap(doc);
    const order = orderArray(doc);
    for (const sheet of workbook.sheets) {
      sheets.set(sheet.id, writeSheet(sheet));
      order.push([sheet.id]);
    }
  }, origin);
}

/** A workbook as one Yjs state, for a document created from a file. */
export function sheetStateFromWorkbook(workbook: SheetWorkbook): Uint8Array {
  assertSheetLimits(workbook);
  const doc = new Y.Doc();
  try {
    writeWorkbook(doc, workbook);
    return Y.encodeStateAsUpdate(doc);
  } finally {
    doc.destroy();
  }
}

/**
 * The sheet ids in tab order. Two people adding a sheet at once both land;
 * an id listed twice shows once, and a sheet missing from the order (a
 * damaged document) still shows, after the rest.
 */
export function sheetOrder(doc: Y.Doc): string[] {
  const sheets = sheetsMap(doc);
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const id of orderArray(doc).toArray()) {
    if (typeof id === "string" && sheets.has(id) && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  for (const id of [...sheets.keys()].toSorted()) if (!seen.has(id)) ids.push(id);
  return ids;
}

/** The live Y.Map of a sheet's cells, or null. */
export function sheetCells(doc: Y.Doc, sheetId: string): Y.Map<SheetCell> | null {
  const cells = sheetsMap(doc).get(sheetId)?.get("cells");
  return cells instanceof Y.Map ? (cells as Y.Map<SheetCell>) : null;
}

/** A sheet's name, declared extent and column widths, without its cells. */
function readSheetHeader(sheet: YSheet) {
  const colWidths: Record<number, number> = {};
  const widths = sheet.get("colWidths");
  if (widths instanceof Y.Map) {
    for (const [col, width] of widths.entries()) {
      if (/^\d+$/.test(col) && typeof width === "number" && width > 0)
        colWidths[Number(col)] = width;
    }
  }
  const count = (value: unknown) =>
    typeof value === "number" && value >= 0 ? Math.trunc(value) : 0;
  const name = sheet.get("name");
  return {
    name: typeof name === "string" && name.trim() ? name : "Sheet",
    rowCount: count(sheet.get("rowCount")),
    colCount: count(sheet.get("colCount")),
    colWidths,
  };
}

export function readSheet(doc: Y.Doc, sheetId: string): SheetData | null {
  const sheet = sheetsMap(doc).get(sheetId);
  if (!(sheet instanceof Y.Map)) return null;
  const cells: Record<string, SheetCell> = {};
  const live = sheet.get("cells");
  if (live instanceof Y.Map) {
    for (const [key, value] of live.entries()) {
      if (parseCellKey(key) !== null && isSheetCell(value)) cells[key] = toCell(value);
    }
  }
  return { id: sheetId, ...readSheetHeader(sheet), cells };
}

export interface SheetMeta {
  readonly name: string;
  readonly rowCount: number;
  readonly colCount: number;
  readonly colWidths: Readonly<Record<number, number>>;
  /** Rows and columns that hold a value (the last filled one + 1). */
  readonly extentRows: number;
  readonly extentCols: number;
  /** Any cell holds a formula. */
  readonly hasFormulas: boolean;
}

/**
 * What the grid needs of a sheet without copying its cells: its name, its
 * extent, column widths and whether it has formulas. Null when it is gone.
 */
export function readSheetMeta(doc: Y.Doc, sheetId: string): SheetMeta | null {
  const sheet = sheetsMap(doc).get(sheetId);
  if (!(sheet instanceof Y.Map)) return null;
  let extentRows = 0;
  let extentCols = 0;
  let hasFormulas = false;
  const live = sheet.get("cells");
  if (live instanceof Y.Map) {
    for (const [key, value] of live.entries()) {
      const at = parseCellKey(key);
      if (at === null || !isSheetCell(value)) continue;
      if (at.row + 1 > extentRows) extentRows = at.row + 1;
      if (at.col + 1 > extentCols) extentCols = at.col + 1;
      if (value.f !== undefined) hasFormulas = true;
    }
  }
  return { ...readSheetHeader(sheet), extentRows, extentCols, hasFormulas };
}

export function readWorkbook(doc: Y.Doc): SheetWorkbook {
  return {
    sheets: sheetOrder(doc)
      .map((id) => readSheet(doc, id))
      .filter((sheet): sheet is SheetData => sheet !== null),
  };
}

export interface SheetCellChange {
  readonly row: number;
  readonly col: number;
  /** Null clears the cell. */
  readonly cell: SheetCell | null;
}

/** Set (or clear) cells of one sheet, in one transaction. False when the sheet is gone. */
export function setSheetCells(
  doc: Y.Doc,
  sheetId: string,
  changes: ReadonlyArray<SheetCellChange>,
  origin: unknown = null,
): boolean {
  const cells = sheetCells(doc, sheetId);
  if (cells === null) return false;
  doc.transact(() => {
    for (const change of changes) {
      if (
        change.row < 0 ||
        change.col < 0 ||
        change.row >= SHEET_LIMITS.rows ||
        change.col >= SHEET_LIMITS.columns
      ) {
        continue;
      }
      const key = cellKey(change.row, change.col);
      if (change.cell === null) {
        if (cells.has(key)) cells.delete(key);
      } else {
        cells.set(key, toCell(change.cell));
      }
    }
  }, origin);
  return true;
}

/** The sheets' names, without reading their cells. */
function sheetNames(doc: Y.Doc, except?: string): string[] {
  const sheets = sheetsMap(doc);
  return sheetOrder(doc)
    .filter((id) => id !== except)
    .map((id) => sheets.get(id)?.get("name"))
    .filter((name): name is string => typeof name === "string");
}

/** Add an empty sheet at the end; returns its id. */
export function addSheet(doc: Y.Doc, name: string, origin: unknown = null): string {
  const id = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  const taken = sheetNames(doc);
  doc.transact(() => {
    sheetsMap(doc).set(
      id,
      writeSheet({
        id,
        name: sheetNameFrom(name, taken),
        rowCount: 0,
        colCount: 0,
        colWidths: {},
        cells: {},
      }),
    );
    orderArray(doc).push([id]);
  }, origin);
  return id;
}

export function renameSheet(
  doc: Y.Doc,
  sheetId: string,
  name: string,
  origin: unknown = null,
): void {
  const sheet = sheetsMap(doc).get(sheetId);
  if (!(sheet instanceof Y.Map)) return;
  doc.transact(() => sheet.set("name", sheetNameFrom(name, sheetNames(doc, sheetId))), origin);
}

/** A new sheet document's first sheet, when it has none yet. */
export function ensureFirstSheet(doc: Y.Doc, origin: unknown = null): string {
  return sheetOrder(doc)[0] ?? addSheet(doc, "Sheet1", origin);
}
